/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * TextInputField
 *
 * @file TextInputField.jsx
 * @author Anuja Gaikwad
 */

import React from 'react';
import PropTypes from 'prop-types';
import { TextField, Typography, InputAdornment, IconButton } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import useStyles from './Styles/AetTextInputFieldStyle';
import Icon from '../icons/Icon';

const TextInputField = ({
  label,
  value,
  onChange,
  placeholder,
  required,
  errorMessage,
  showEditIcon,
  titleHelperText,
  styles,
  disabled,
  ...rest
}) => {
  const classes = useStyles();
  const hasError = !!errorMessage;

  const renderLabel = () => {
    if (!label) {
      return null;
    }

    return (
      <Typography className={classes.label}>
        {label}
      </Typography>
    );
  };

  const renderErrorMessage = () => {
    if (!hasError || !errorMessage) {
      return null;
    }

    return (
      <Typography className={classes.errorText} role="alert">
        <WarningIcon className={classes.errorIcon} />
        {errorMessage}
      </Typography>
    );
  };

  const renderEndAdornment = () => {
    if (!showEditIcon) {
      return null;
    }

    return (
      <InputAdornment position="end">
        <IconButton tabIndex={-1} edge="end" aria-label="edit" className={classes.editIconContainer}>
          <Icon name="EditActionIcon" />
        </IconButton>
      </InputAdornment>
    );
  };

  const renderHelperText = () => {
    if (hasError) {
      return null; // prevent duplicate display
    }

    if (!titleHelperText) {
      return null;
    }

    return (
      <Typography className={classes.helperText}>
        {titleHelperText}
      </Typography>
    );
  };

  return (
    <div className={classes.inputWrapper} style={styles}>
      {renderLabel()}

      <div className={classes.textInputContainer}>
        <TextField
          className={`${disabled ? classes.disabledTextfield : ''}`}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          variant="outlined"
          fullWidth
          required={required}
          error={hasError}
          placeholder={placeholder}
          InputProps={{
            classes: {
              root: classes.outlinedInputRoot,
              focused: classes.focused,
              error: classes.error,
              notchedOutline: classes.notchedOutline,
              input: classes.outlinedInput,
              disabled: classes.disabledTextInput
            },
            endAdornment: renderEndAdornment()
          }}
          {...rest}
        />
      </div>

      {renderErrorMessage()}
      {renderHelperText()}
    </div>
  );
};

TextInputField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  errorMessage: PropTypes.string,
  showEditIcon: PropTypes.bool,
  styles: PropTypes.object,
  titleHelperText: PropTypes.string,
  disabled: PropTypes.bool
};

TextInputField.defaultProps = {
  label: '',
  placeholder: '',
  required: false,
  errorMessage: '',
  showEditIcon: false,
  styles: {},
  titleHelperText: '',
  disabled: false
};

export default TextInputField;
